import { createSlice } from '@reduxjs/toolkit';

// Load draft from localStorage if available
const loadDraftFromStorage = () => {
  try {
    const savedDraft = localStorage.getItem('courseDraftFormData');
    return savedDraft ? JSON.parse(savedDraft) : null;
  } catch (error) {
    console.error('Error loading course draft from localStorage:', error);
    return null;
  }
};

const initialState = {
  courses: [],
  currentCourse: null,
  isLoading: false,
  error: null,
  draftFormData: loadDraftFromStorage(), // Load draft from localStorage on init
  filters: {
    search: '',
    category: '',
    level: '',
    format: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
};

const courseSlice = createSlice({
  name: 'course',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentCourse: (state) => {
      state.currentCourse = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setLimit: (state, action) => {
      state.pagination.limit = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1; // Reset to first page when changing page size
    },
    resetPagination: (state) => {
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
      };
    },
    // Save draft form data
    saveCourseDraft: (state, action) => {
      state.draftFormData = action.payload;
      // Also save to localStorage for persistence across page refreshes
      try {
        localStorage.setItem('courseDraftFormData', JSON.stringify(action.payload));
      } catch (error) {
        console.error('Error saving course draft to localStorage:', error);
      }
    },
    // Clear draft form data
    clearCourseDraft: (state) => {
      state.draftFormData = null;
      // Also remove from localStorage
      try {
        localStorage.removeItem('courseDraftFormData');
      } catch (error) {
        console.error('Error clearing course draft from localStorage:', error);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Courses
      .addCase('course/getCourses/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('course/getCourses/fulfilled', (state, action) => {
        state.isLoading = false;
        state.courses = action.payload.data || action.payload;
        state.pagination = action.payload.pagination || state.pagination;
        state.error = null;
      })
      .addCase('course/getCourses/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch courses';
      })
      // Fetch Courses with Filters
      .addCase('course/fetchCoursesWithFilters/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('course/fetchCoursesWithFilters/fulfilled', (state, action) => {
        state.isLoading = false;
        state.courses = action.payload.data || action.payload;
        state.pagination = action.payload.pagination || state.pagination;
        state.error = null;
      })
      .addCase('course/fetchCoursesWithFilters/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch courses';
      })
      // Get Course by ID
      .addCase('course/getCourseById/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('course/getCourseById/fulfilled', (state, action) => {
        state.isLoading = false;
        const courseData = action.payload?.data || action.payload;
        // Preserve existing lessons if new course data doesn't have them or if they're incomplete
        // Only preserve if courseId matches and we have existing lessons
        if (state.currentCourse?.lessons && courseData && state.currentCourse._id === courseData._id) {
          // If new course doesn't have lessons, or has empty lessons array, or has array of strings (IDs), preserve existing
          const isArrayOfStrings = Array.isArray(courseData.lessons) && courseData.lessons.length > 0 && typeof courseData.lessons[0] === 'string';
          if (!courseData.lessons || (Array.isArray(courseData.lessons) && courseData.lessons.length === 0) || isArrayOfStrings) {
            courseData.lessons = state.currentCourse.lessons;
          }
        }
        state.currentCourse = courseData;
        state.error = null;
      })
      .addCase('course/getCourseById/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch course';
      })
      // Create Course
      .addCase('course/createCourse/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('course/createCourse/fulfilled', (state, action) => {
        state.isLoading = false;
        state.courses.unshift(action.payload); // Add new course to the beginning
        state.error = null;
      })
      .addCase('course/createCourse/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create course';
      })
      // Update Course
      .addCase('course/updateCourse/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('course/updateCourse/fulfilled', (state, action) => {
        state.isLoading = false;
        const index = state.courses.findIndex(course => course._id === action.payload._id);
        if (index !== -1) {
          state.courses[index] = action.payload;
        }
        state.error = null;
      })
      .addCase('course/updateCourse/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update course';
      })
      // Delete Course
      .addCase('course/deleteCourse/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('course/deleteCourse/fulfilled', (state, action) => {
        state.isLoading = false;
        state.courses = state.courses.filter(course => course._id !== action.payload);
        state.error = null;
      })
      .addCase('course/deleteCourse/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete course';
      });
      
      // Lessons in Course - Get all
      builder
      .addCase('course/getAllLessonsInCourse/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('course/getAllLessonsInCourse/fulfilled', (state, action) => {
        state.isLoading = false;
        if (state.currentCourse && state.currentCourse._id === action.payload.courseId) {
          // Handle different response structures
          let lessonsData = action.payload.lessons;
          
          // Extract lessons from different possible response structures
          if (lessonsData?.data && Array.isArray(lessonsData.data)) {
            lessonsData = lessonsData.data;
          } else if (lessonsData?.lessons && Array.isArray(lessonsData.lessons)) {
            lessonsData = lessonsData.lessons;
          } else if (Array.isArray(lessonsData)) {
            lessonsData = lessonsData;
          } else {
            lessonsData = [];
          }
          
          // Always update lessons array, even if empty - this ensures fresh data
          if (Array.isArray(lessonsData)) {
            state.currentCourse.lessons = lessonsData;
          } else if (!state.currentCourse.lessons) {
            // If lessonsData is not an array and we don't have existing lessons, set empty array
            state.currentCourse.lessons = [];
          }
        } else if (!state.currentCourse) {
          // If currentCourse doesn't exist yet, we should still handle the error
          // This shouldn't happen, but let's be safe
          console.warn('Cannot update lessons: currentCourse is null');
        }
      })
      .addCase('course/getAllLessonsInCourse/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to load course lessons';
      })
      
      // Lessons in Course - Add existing
      .addCase('course/addExistingLessonToCourse/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('course/addExistingLessonToCourse/fulfilled', (state, action) => {
        state.isLoading = false;
        if (state.currentCourse && state.currentCourse._id === action.payload.courseId) {
          state.currentCourse.lessons = state.currentCourse.lessons || [];
          state.currentCourse.lessons.push(action.payload.lesson);
        }
      })
      .addCase('course/addExistingLessonToCourse/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to add lesson to course';
      })
      
      // Lessons in Course - Create new and add
      .addCase('course/createNewLessonAndAddToCourse/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('course/createNewLessonAndAddToCourse/fulfilled', (state, action) => {
        state.isLoading = false;
        if (state.currentCourse && state.currentCourse._id === action.payload.courseId) {
          state.currentCourse.lessons = state.currentCourse.lessons || [];
          state.currentCourse.lessons.push(action.payload.lesson);
        }
      })
      .addCase('course/createNewLessonAndAddToCourse/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create lesson in course';
      })
      
      // Lessons in Course - Update
      .addCase('course/updateLessonInCourse/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('course/updateLessonInCourse/fulfilled', (state, action) => {
        state.isLoading = false;
        if (state.currentCourse && state.currentCourse._id === action.payload.courseId && state.currentCourse.lessons) {
          const idx = state.currentCourse.lessons.findIndex(l => l._id === action.payload.lesson._id);
          if (idx !== -1) state.currentCourse.lessons[idx] = action.payload.lesson;
        }
      })
      .addCase('course/updateLessonInCourse/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update lesson in course';
      })
      
      // Lessons in Course - Remove
      .addCase('course/removeLessonFromCourse/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('course/removeLessonFromCourse/fulfilled', (state, action) => {
        state.isLoading = false;
        if (state.currentCourse && state.currentCourse._id === action.payload.courseId && state.currentCourse.lessons) {
          state.currentCourse.lessons = state.currentCourse.lessons.filter(l => l._id !== action.payload.lessonId);
        }
      })
      .addCase('course/removeLessonFromCourse/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to remove lesson from course';
      })
      
      // Course with lessons and HLS video links
      .addCase('course/getCourseWithLessonsAndHSLVideoLinks/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('course/getCourseWithLessonsAndHSLVideoLinks/fulfilled', (state, action) => {
        state.isLoading = false;
        state.currentCourse = action.payload;
      })
      .addCase('course/getCourseWithLessonsAndHSLVideoLinks/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to load course with videos';
      });
  },
});

export const { 
  clearError, 
  clearCurrentCourse, 
  setFilters, 
  setPagination, 
  setPage, 
  setLimit, 
  setCurrentPage, 
  setItemsPerPage, 
  resetPagination,
  saveCourseDraft,
  clearCourseDraft
} = courseSlice.actions;

export default courseSlice.reducer;
